import React, { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { View, StyleSheet, LayoutAnimation, } from 'react-native';
import Animated from 'react-native-reanimated';
import SectionHeader from 'design-system/SectionHeader';
import { TextButtonLink } from 'design-system/TextButton';
import { rem } from 'design-system/values';
import { useCategories } from 'hooks/useCategory';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector } from 'store/hooks';
import { selectNormalizedCategories, selectVisibleParentCategories, } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useIsTablet from 'hooks/useIsTablet';
import { Button } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { defaultCategories, isDesktopOrWeb, isWeb, } from '../../../../constants';
import EditCategoryRow from './EditCategoryRow';
import PickSubcategoriesSection from './PickSubcategoriesSection';
const keyExtractor = (item) => item[0].id;
const splitIntoRows = (array) => array.reduce((curr, item, index) => {
    const chunkIndex = Math.floor(index / 4);
    const newArray = curr;
    if (!newArray[chunkIndex]) {
        newArray[chunkIndex] = []; // new row
    }
    newArray[chunkIndex].push(item);
    return newArray;
}, []);
const EditCategoryList = ({ scrollHandler, contentContainerStyle, selectedCategory, onPressCategory, onLongPressCategory, onPressManage, title = 'Pick a category', subTitle, firstItem, hideExcluded = false, hideSubcategories = false, hiddenCategories, buttonTitle = 'Save', isInIOS13Modal = false, }) => {
    const [currentSelection, setCurrentSelection] = useState(selectedCategory);
    const scrollRef = useRef(null);
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const visibleParentCategories = useAppSelector(selectVisibleParentCategories);
    const fallbackCategories = useCategories();
    const { width } = useAppFrameDimensions(isWeb, isInIOS13Modal);
    const isTablet = useIsTablet();
    const handlePressCategory = useCallback((data, isDefaultCategory) => {
        if ('customContent' in data || hideSubcategories) {
            onPressCategory(data, Boolean(isDefaultCategory));
            return;
        }
        const subcategories = normalizedCategories[data.id]?.subCategoryIds;
        if (subcategories?.length) {
            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
            if (currentSelection?.id !== data.id &&
                !subcategories?.includes(currentSelection?.id ?? '')) {
                setCurrentSelection(data);
            }
            return;
        }
        if ('parentCategoryId' in data && data.parentCategoryId) {
            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
            if (currentSelection?.id === data.id) {
                setCurrentSelection(normalizedCategories[data.parentCategoryId]);
            }
            else {
                setCurrentSelection(data);
            }
            return;
        }
        onPressCategory(data, Boolean(isDefaultCategory));
    }, [
        currentSelection?.id,
        hideSubcategories,
        normalizedCategories,
        onPressCategory,
    ]);
    const hasScreenMounted = useRef(false);
    useEffect(() => {
        hasScreenMounted.current = true;
    }, []);
    const renderRow = useCallback(({ item: row, index }) => {
        let parentIndex;
        const foundParent = row.find((item, index) => {
            if (item.id ===
                (currentSelection?.parentCategoryId ?? currentSelection?.id)) {
                parentIndex = index;
                return item;
            }
            return null;
        });
        const subcategories = foundParent
            ? normalizedCategories[foundParent.id]?.subCategoryIds
            : undefined;
        const hasSubcategories = !!subcategories?.length;
        return (<View>
          <View style={styles.row}>
            {row.map((item) => {
                const category = fallbackCategories[item.id] || item;
                const id = foundParent?.id != null ? foundParent.id : null;
                return (<EditCategoryRow key={item.id} onLongPress={onLongPressCategory} isDefaultCategory={defaultCategories.includes(item.id)} onPress={handlePressCategory} item={category} selected={id === category.id}/>);
            })}
            {Array.from({ length: 4 - row.length }).map((_, i) => (<View key={i} style={{
                    width: isDesktopOrWeb || isTablet
                        ? (width - rem(32)) / 4
                        : rem(76),
                }}/>))}
          </View>

          {!hideSubcategories &&
                !!hasSubcategories &&
                !!foundParent &&
                typeof parentIndex === 'number' && (<PickSubcategoriesSection scrollRef={scrollRef} parentCategory={foundParent} subcategories={subcategories
                    .map((id) => normalizedCategories[id])
                    .filter(Boolean)} onPressCategory={handlePressCategory} selectedCategoryId={currentSelection?.id} rowIndex={index} parentIndex={parentIndex} hasScreenMounted={hasScreenMounted.current}/>)}
        </View>);
    }, [
        currentSelection?.id,
        currentSelection?.parentCategoryId,
        fallbackCategories,
        handlePressCategory,
        hideSubcategories,
        isTablet,
        normalizedCategories,
        onLongPressCategory,
        width,
    ]);
    const renderHeader = useCallback(() => (<>
        <ScreenInfoHeader title={title} subTitle={subTitle}/>
        {!!onPressManage && (<SectionHeader style={styles.sectionHeader} largeVerticalMargins={isWeb} button={<TextButtonLink text="Manage" onPress={onPressManage}/>}>
            Categories
          </SectionHeader>)}
      </>), [onPressManage, subTitle, title]);
    const onContinue = useCallback(() => {
        if (!currentSelection)
            return;
        onPressCategory(currentSelection, defaultCategories.includes(currentSelection.id));
    }, [currentSelection, onPressCategory]);
    const data = useMemo(() => {
        let categoriesToUse = visibleParentCategories?.length
            ? visibleParentCategories
            : Object.values(fallbackCategories);
        if (hideExcluded) {
            categoriesToUse = categoriesToUse.filter((category) => category.id !== 'internal' && category.id !== 'transfer');
        }
        if (hiddenCategories) {
            categoriesToUse = categoriesToUse.filter((category) => !hiddenCategories.includes(category.id));
        }
        if (firstItem) {
            return splitIntoRows([firstItem].concat(categoriesToUse));
        }
        return splitIntoRows(categoriesToUse);
    }, [
        fallbackCategories,
        firstItem,
        hiddenCategories,
        hideExcluded,
        visibleParentCategories,
    ]);
    return (<>
      <Animated.FlatList ref={scrollRef} renderItem={renderRow} scrollEventThrottle={16} onScroll={scrollHandler} keyExtractor={keyExtractor} data={data} ListHeaderComponent={renderHeader} contentContainerStyle={[
            contentContainerStyle,
            !hideSubcategories && {
                paddingBottom: (Number(StyleSheet.flatten(contentContainerStyle).paddingBottom) || 0) + HEIGHT_PLUS_PADDING,
            },
        ]}/>
      {!hideSubcategories &&
            !!currentSelection &&
            currentSelection?.id !== selectedCategory?.id && (<Button brand floating title={buttonTitle} onPress={onContinue} style={styles.button} isInIOS13Modal={isInIOS13Modal}/>)}
    </>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    sectionHeader: {
        marginTop: rem(12),
        marginHorizontal: rem(16),
        marginBottom: rem(24),
    },
    button: {
        zIndex: 1,
    },
});
export default memo(EditCategoryList);
